import { MediaServices } from "@/services/MediaServices";
import { StoreException } from "@/utils/ErrorHandler";
import { FileExtentions } from "@/utils/GeneralConstants";
import IdUtils from "@/utils/IdUtils";
import {
  validateMediaNameOrPath,
  validateStoreInput,
  checkMimeType,
} from "@/utils/GeneralUtils";

const state = {
  mediaServices: null,
  media: [],
  omnimedia: [],
  omnimediaIds: [],
  mediaToBeDeleted: [],
  uploadingMedia: false,
  isActivity: true,
};

const getters = {
  mediaServices: (state) => state.mediaServices,
  media: (state) => state.media,
  omnimedia: (state) => state.omnimedia,
  omnimediaIds: (state) => state.omnimediaIds,
  mediaToBeDeleted: (state) => state.mediaToBeDeleted,
  uploadingMedia: (state) => state.uploadingMedia,
  isActivity: (state) => state.isActivity,
};

const actions = {
  /**
   * Internal action for initializing and returning the MediaServices API object.
   * @param getters
   * @param rootGetters
   * @param commit
   * @return {Promise<getters.mediaServices|(function(*): (null|getters.mediaServices|(function(*))))|null>}
   */
  getMediaServices: async ({ getters, rootGetters, commit }) => {
    try {
      if (null === getters.mediaServices) {
        if ("" === rootGetters.tenantId) {
          throw new StoreException("No tenant id known.");
        } else {
          commit(
            "SET_MEDIA_SERVICES",
            new MediaServices(rootGetters.organisationId)
          );
        }
      }

      return getters.mediaServices;
    } catch (error) {
      console.error(error);
    }
  },

  /**
   * Retrieve media by instance id
   *
   * @param commit
   * @param getters
   * @param dispatch
   * @param fileId
   * @returns media
   */
  retrieveMedia: async (
    { commit, dispatch },
    { instanceId, pageSize, pageNumber }
  ) => {
    const mediaServices = await dispatch("getMediaServices");
    try {
      const media = await mediaServices.getMedia(
        instanceId,
        pageSize,
        pageNumber
      );
      commit("SET_MEDIA", media);
      return media;
    } catch (error) {
      throw new StoreException(
        "Something went wrong while retrieving file: \n" + error.message
      );
    }
  },

  retrieveFile: async ({ getters, dispatch }, { fileId, pdf = false }) => {
    const mediaServices = await dispatch("getMediaServices");
    try {
      const selectedMedia = getters.media.data.find(
        (media) => media.identification === fileId
      );

      return await mediaServices.retrieveFile({ fileId: fileId, pdf: pdf });
    } catch (error) {
      throw new StoreException(
        "Something went wrong while retrieving file: \n" + error.message
      );
    }
  },

  deleteMedia: async ({ dispatch }, medium_identification) => {
    if (medium_identification) {
      try {
        const mediaServices = await dispatch("getMediaServices");
        await mediaServices.deleteMedia(medium_identification);
      } catch (error) {
        throw new StoreException(
          "Something went wrong while trying to delete media file: \n" +
            error.message
        );
      }
    }
  },

  createMedia: async ({ dispatch }, { instanceId, metadata, file }) => {
    const mediaServices = await dispatch("getMediaServices");

    if (!instanceId) {
      throw new StoreException("No instance id given.");
    } else if (!metadata.name) {
      throw new StoreException("No name given.");
      // } else if (!metadata.definition_code) {
    } else if (!metadata.relationships.definition) {
      throw new StoreException("No medium definition given.");
    } else if (!file) {
      throw new StoreException("No files given.");
    } else {
      // Check metadata (mimetype)
      if (!metadata.mime_type) {
        // Check for common file extensions not supported by upload component
        const fileExt = metadata.name.slice(metadata.name.lastIndexOf(".") + 1);
        if (FileExtentions[fileExt]) {
          metadata.mime_type = FileExtentions[fileExt];
        } else {
          throw new StoreException("Unsupported media type");
        }
      }

      try {
        // Post metadata
        metadata.path = metadata.path ? metadata.path : "";
        const newMedium = await mediaServices.createMediaMetadata(
          instanceId,
          metadata
        );

        // Create formData for files payload
        const formData = new FormData();
        formData.append("content", file);
        formData.append("contentName", metadata.name);
        formData.append("contentType", metadata.mime_type);
        formData.append("path", metadata.path);

        // Post media binary using the new medium metadata object
        await mediaServices.createMediaBinary(
          newMedium.identification,
          formData
        );
      } catch (error) {
        throw new StoreException(error);
      }
    }
  },

  createMediaV3: async ({ dispatch }, body) => {
    try {
      if (!body) {
        throw new StoreException("Invalid input");
      }
      const mediaServices = await dispatch("getMediaServices");
      await mediaServices.linkMedia(body);
    } catch (error) {
      throw new StoreException(error);
    }
  },

  linkMedia: async (
    { dispatch },
    { targetId, metadata, linkType, mediaDefinitionId }
  ) => {
    if (
      !IdUtils.isUUID(targetId) ||
      !IdUtils.isUUID(metadata?.identification) ||
      !linkType ||
      !IdUtils.isUUID(mediaDefinitionId)
    ) {
      throw new StoreException("Invalid input");
    }
    try {
      const payload = {
        link_target: targetId,
        medium_identification: metadata.identification,
        link_type: linkType,
        path: "/",
        medium_definition: mediaDefinitionId,
        metadata: metadata,
      };
      const mediaServices = await dispatch("getMediaServices");
      await mediaServices.linkMedia(payload);
    } catch (error) {
      throw new StoreException(error);
    }
  },

  retrieveMediumMetadata: async ({ commit, getters, dispatch }, { fileId }) => {
    if (!IdUtils.isUUID(fileId)) {
      throw new StoreException("Invalid input");
    }
    try {
      const mediaServices = await dispatch("getMediaServices");
      return mediaServices.retrieveMediumMetadata(fileId);
    } catch (error) {
      throw new StoreException(
        "Something went wrong while retrieving metadata: \n" + error.message
      );
    }
  },

  setOmnimedia({ commit, getters }, payload) {
    if (!Array.isArray(payload)) {
      const media = getters.omnimedia.slice();
      media.push(payload);
      commit("SET_OMNIMEDIA", media);
    } else {
      commit("SET_OMNIMEDIA", payload);
    }
  },
  setOmnimediaId({ commit, getters }, payload) {
    if (!Array.isArray(payload)) {
      const ids = getters.omnimediaIds.slice();
      ids.push(payload);
      commit("SET_OMNIMEDIA_IDS", ids);
    } else {
      commit("SET_OMNIMEDIA_IDS", payload);
    }
  },
  removeOmnimediaId({ commit, getters }, payload) {
    let ids = getters.omnimediaIds.slice();
    ids = ids.filter((id) => id.id !== payload);
    commit("SET_OMNIMEDIA_IDS", ids);
  },
  setMediaToBeDeleted({ commit, getters }, payload) {
    if (!Array.isArray(payload)) {
      const ids = getters.mediaToBeDeleted.slice();
      ids.push(payload);
      commit("SET_DELETE_MEDIA", ids);
    } else {
      commit("SET_DELETE_MEDIA", payload);
    }
  },

  async createMediumContent({ dispatch }, { instanceId, metadata, file }) {
    // Validate input
    if (!IdUtils.isUUID(instanceId) || !metadata || !file) {
      throw new StoreException("Invalid input for createMediumContent");
    }
    validateStoreInput(metadata, ["filename", "definition_code"]);
    // Check metadata for unsafe characters
    if (
      !validateMediaNameOrPath(metadata.filename) ||
      (metadata.path && !validateMediaNameOrPath(metadata.path))
    ) {
      throw new StoreException("Invalid filename and/or path");
    }

    // Check metadata (mimetype)
    metadata = checkMimeType(metadata);

    const mediaServices = await dispatch("getMediaServices");
    try {
      // Create formData for files payload
      const formData = new FormData();
      formData.append("content", file);
      formData.append("metadata", JSON.stringify(metadata));
      // Post media binary and metadata
      return mediaServices.uploadMediumContent(instanceId, formData);
    } catch (error) {
      throw new StoreException(
        "Something went wrong while uploading media file: \n" + error.message
      );
    }
  },

  setUploadingMedia({ commit, getters }, isUploading) {
    commit("SET_UPLOADING_MEDIA", isUploading);
  },
  setIsActivity({ commit }, payload) {
    commit("SET_IS_ACTIVITY", payload);
  },
};
const mutations = {
  SET_MEDIA_SERVICES: (state, payload) => (state["mediaServices"] = payload),
  SET_MEDIA: (state, payload) => (state["media"] = payload),
  SET_OMNIMEDIA: (state, payload) => (state["omnimedia"] = payload),
  SET_OMNIMEDIA_IDS: (state, payload) => (state["omnimediaIds"] = payload),
  SET_DELETE_MEDIA: (state, payload) => (state["mediaToBeDeleted"] = payload),
  SET_UPLOADING_MEDIA: (state, payload) => (state["uploadingMedia"] = payload),
  SET_IS_ACTIVITY: (state, payload) => (state["isActivity"] = payload),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
